import { dashboardApi } from '@/store/api/base/api.base';
import type {
    TypeAccount, TypeAccountAccessEvent, TypeAccountConnection, TypeAccountListed, TypeAccountProfile, TypeAccountSession, TypeRecoveryCodes,
} from '@/types/auth/types.auth.account';

const segment = (value: string): string => encodeURIComponent(value);

export const accountApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        readAccount: build.query<TypeAccount, void>({
            query: () => ({ url: '/auth/account' }),
            providesTags: ['Account', 'Passkey'],
        }),

        updateAccount: build.mutation<TypeAccountProfile, { firstname?: string; lastname?: string; locale?: string }>({
            query: (patch) => ({ url: '/auth/account', method: 'patch', data: patch }),
            invalidatesTags: ['Account'],
        }),

        readAccountAccess: build.query<TypeAccountListed<TypeAccountAccessEvent>, { cursor: string; limit: number }>({
            query: ({ cursor, limit }) => ({ url: '/auth/account/access', params: { limit, ...(cursor ? { cursor } : {}) } }),
            providesTags: ['Access'],
        }),

        renamePasskey: build.mutation<unknown, { id: string; name: string }>({
            query: ({ id, name }) => ({ url: `/auth/account/passkeys/${segment(id)}`, method: 'patch', data: { name } }),
            invalidatesTags: ['Passkey'],
        }),

        removePasskey: build.mutation<unknown, { id: string }>({
            query: ({ id }) => ({ url: `/auth/account/passkeys/${segment(id)}`, method: 'delete' }),
            invalidatesTags: ['Passkey', 'Account'],
        }),

        disableTotp: build.mutation<unknown, void>({
            query: () => ({ url: '/auth/account/totp', method: 'delete' }),
            invalidatesTags: ['Account'],
        }),

        regenerateRecoveryCodes: build.mutation<TypeRecoveryCodes, void>({
            query: () => ({ url: '/auth/account/recovery-codes', method: 'post' }),
            invalidatesTags: ['Account'],
        }),

        confirmAccountEmail: build.mutation<{ email: string }, { token: string }>({
            query: (payload) => ({ url: '/auth/account/email/confirm', method: 'post', data: payload }),
            invalidatesTags: ['Account'],
        }),

        listAccountSessions: build.query<TypeAccountListed<TypeAccountSession>, void>({
            query: () => ({ url: '/auth/sessions' }),
            providesTags: ['Session'],
        }),

        revokeAccountSession: build.mutation<unknown, { sid: string }>({
            query: ({ sid }) => ({ url: `/auth/sessions/${segment(sid)}`, method: 'delete' }),
            invalidatesTags: ['Session'],
        }),

        revokeOtherSessions: build.mutation<{ revoked: number }, void>({
            query: () => ({ url: '/auth/sessions', method: 'delete' }),
            invalidatesTags: ['Session'],
        }),

        listAccountConnections: build.query<TypeAccountListed<TypeAccountConnection>, void>({
            query: () => ({ url: '/auth/account/connections' }),
            providesTags: ['Connection'],
        }),

        revokeAccountConnection: build.mutation<unknown, { grantId: string }>({
            query: ({ grantId }) => ({ url: `/auth/account/connections/${segment(grantId)}`, method: 'delete' }),
            invalidatesTags: ['Connection'],
        }),
    }),
});

export const {
    useReadAccountQuery,
    useUpdateAccountMutation,
    useReadAccountAccessQuery,
    useRenamePasskeyMutation,
    useRemovePasskeyMutation,
    useDisableTotpMutation,
    useRegenerateRecoveryCodesMutation,
    useConfirmAccountEmailMutation,
    useListAccountSessionsQuery,
    useRevokeAccountSessionMutation,
    useRevokeOtherSessionsMutation,
    useListAccountConnectionsQuery,
    useRevokeAccountConnectionMutation,
} = accountApi;

export default accountApi;
